import { Modal, Stack, Text, Group, Badge, Button } from '@mantine/core';
import { Download, Smartphone, HelpCircle } from 'lucide-react';
import { t, type Lang } from '../i18n';
import { getDeviceInfo } from '../utils/deviceDetection';

interface InstallHelpModalProps {
    lang: Lang;
    opened: boolean;
    onClose: () => void;
}

export const InstallHelpModal = ({ lang, opened, onClose }: InstallHelpModalProps) => {
    const device = getDeviceInfo();

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <Group gap="xs">
                    <Download size={20} style={{ color: 'var(--mantine-color-blue-6)' }} />
                    <span>{t(lang, 'install_help_title')}</span>
                </Group>
            }
            size="md"
            centered
        >
            <Stack gap="lg">
                {/* Description */}
                <Text color="dimmed" size="sm">
                    {t(lang, 'install_help_description')}
                </Text>

                {/* iOS Instructions */}
                {(device.isIOS || device.isIPad) && (
                    <Stack gap="xs" style={{ backgroundColor: 'var(--mantine-color-blue-0)', padding: '12px', borderRadius: '8px' }}>
                        <Group gap="xs">
                            <Badge color="blue" size="lg">
                                iOS
                            </Badge>
                            <Smartphone size={18} style={{ color: 'var(--mantine-color-blue-6)' }} />
                        </Group>
                        <Text component="div" size="sm" style={{ color: 'black' }}>
                            <strong>{t(lang, 'install_help_ios')}</strong>
                        </Text>
                        <Text size="xs" style={{ color: '#333' }}>
                            {t(lang, 'install_help_detected_ios')}
                        </Text>
                        <Stack gap={4} style={{ paddingLeft: '12px', borderLeft: '2px solid var(--mantine-color-blue-5)' }}>
                            <Text size="sm" style={{ color: 'black' }}>
                                {t(lang, 'install_help_ios_step1')}
                            </Text>
                            <Text size="sm" style={{ color: 'black' }}>
                                {t(lang, 'install_help_ios_step2')}
                            </Text>
                            <Text size="sm" style={{ color: 'black' }}>
                                {t(lang, 'install_help_ios_step3')}
                            </Text>
                            <Text size="sm" style={{ color: 'black' }}>
                                {t(lang, 'install_help_ios_step4')}
                            </Text>
                        </Stack>
                    </Stack>
                )}

                {/* Android Instructions */}
                {device.isAndroid && !device.isIOS && (
                    <Stack gap="xs" style={{ backgroundColor: 'var(--mantine-color-green-0)', padding: '12px', borderRadius: '8px' }}>
                        <Group gap="xs">
                            <Badge color="green" size="lg">
                                Android
                            </Badge>
                            <Smartphone size={18} style={{ color: 'var(--mantine-color-green-6)' }} />
                        </Group>
                        <Text component="div" size="sm" style={{ color: 'black' }}>
                            <strong>{t(lang, 'install_help_android')}</strong>
                        </Text>
                        <Text size="xs" style={{ color: '#333' }}>
                            {t(lang, 'install_help_detected_android')}
                        </Text>
                        <Stack gap={4} style={{ paddingLeft: '12px', borderLeft: '2px solid var(--mantine-color-green-5)' }}>
                            <Text size="sm" style={{ color: 'black' }}>
                                {t(lang, 'install_help_android_step1')}
                            </Text>
                            <Text size="sm" style={{ color: 'black' }}>
                                {t(lang, 'install_help_android_step2')}
                            </Text>
                            <Text size="sm" style={{ color: 'black' }}>
                                {t(lang, 'install_help_android_step3')}
                            </Text>
                            <Text size="sm" style={{ color: 'black' }}>
                                {t(lang, 'install_help_android_step4')}
                            </Text>
                        </Stack>
                    </Stack>
                )}

                {/* Generic Instructions for non-mobile/detected devices */}
                {!device.isIOS && !device.isAndroid && !device.isIPad && (
                    <Stack gap="xs" style={{ backgroundColor: 'var(--mantine-color-gray-0)', padding: '12px', borderRadius: '8px' }}>
                        <Group gap="xs">
                            <Badge color="gray" size="lg">
                                {t(lang, 'install_help_general_instructions')}
                            </Badge>
                            <HelpCircle size={18} style={{ color: 'var(--mantine-color-gray-6)' }} />
                        </Group>
                        <Text size="sm" color="dimmed">
                            {t(lang, 'install_help_access_from_phone')}
                        </Text>
                        <Stack gap={4}>
                            <Text size="sm" component="div">
                                <strong>{lang === 'fr' ? 'iOS:' : 'iOS:'}</strong>
                            </Text>
                            <Text size="sm" color="dimmed" style={{ paddingLeft: '12px' }}>
                                {t(lang, 'install_help_ios')}
                            </Text>

                            <Text size="sm" component="div" style={{ marginTop: '8px' }}>
                                <strong>{lang === 'fr' ? 'Android:' : 'Android:'}</strong>
                            </Text>
                            <Text size="sm" color="dimmed" style={{ paddingLeft: '12px' }}>
                                {t(lang, 'install_help_android')}
                            </Text>
                        </Stack>
                    </Stack>
                )}

                {/* Benefits Section */}
                <Stack gap="xs">
                    <Text size="sm" fw={500}>
                        {t(lang, 'install_help_benefits_title')}
                    </Text>
                    <Stack gap={4} style={{ paddingLeft: '12px' }}>
                        <Text size="sm" color="dimmed">
                            {t(lang, 'install_help_benefit_quick_access')}
                        </Text>
                        <Text size="sm" color="dimmed">
                            {t(lang, 'install_help_benefit_performance')}
                        </Text>
                        <Text size="sm" color="dimmed">
                            {t(lang, 'install_help_benefit_offline')}
                        </Text>
                        <Text size="sm" color="dimmed">
                            {t(lang, 'install_help_benefit_fullscreen')}
                        </Text>
                    </Stack>
                </Stack>

                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose}>
                        {t(lang, 'cancel')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
